import { compact, difference, intersection, uniq, uniqBy } from "lodash-es";
import { acceptHMRUpdate, defineStore } from "pinia";
import { type CustomEventDataType, SSE } from "sse-ts";
import { computed, reactive, ref, toRefs } from "vue";
import { useRouter } from "vue-router";

import { Cancellation } from "@/api";
import { useCompanyDictionaryAd, useSingleton } from "@/composables";
import { DictionariesHelper, EntitiesHelper, SearchFilterHelper, SearchHelper, SearchRouteHelper } from "@/helpers";
import { SearchService } from "@/services";
import {
  useDictionariesStore,
  useFavoriteDictionariesStore,
  useSearchFilterStore,
  useSuggestionsStore,
  useUserStore
} from "@/stores";
import type {
  Article,
  Dictionary,
  DictionaryMethod,
  SearchFilter,
  SearchResults,
  UpsellArticle,
  UpsellArticleGroup,
  UpsellResult
} from "@/types";
import { flushVueQueue } from "@/utils";

interface State {
  status: "initial" | "loading" | "loaded" | "failed";
  query: string;
  filter?: SearchFilter;
  selectedDictionaryId?: string;
  staticMetadata: Record<string, string>;
  temporaryMetadata: Record<string, string[]>;
  section?: string;
  switchedFromDictionaryId?: string;
  scopedSearchMethods: DictionaryMethod[];
  results?: SearchResults;
  upsellResults?: UpsellResult;
}

const classicSearchSingleton = useSingleton(SearchService.classicSearch);
const upsellSearchSingleton = useSingleton(SearchService.upsellSearch);

const MAX_SUGGESTIONS_COUNT = 8;
const UPSELL_THRESHOLD = 1;
const UPSELL_MAX_SUGGESTIONS_COUNT = 3;

export const useSearchStore = defineStore("search", () => {
  const { dictionaryId: companyDictionaryAdId } = useCompanyDictionaryAd();

  const searchFilterStore = useSearchFilterStore();
  const userStore = useUserStore();

  const searchRouteHelper = new SearchRouteHelper(useRouter(), userStore.userData);

  const state = reactive<State>({
    status: "initial",
    query: "",
    filter: searchFilterStore.filter,
    selectedDictionaryId: undefined,
    staticMetadata: {},
    temporaryMetadata: {},
    section: undefined,
    switchedFromDictionaryId: undefined,
    scopedSearchMethods: [],
    results: undefined,
    upsellResults: undefined
  });

  const dictionariesStore = useDictionariesStore();
  const favoriteDictionariesStore = useFavoriteDictionariesStore();
  const suggestionsStore = useSuggestionsStore();

  const defaultTitle = document.title;

  const reset = () => {
    state.status = "initial";
    state.query = "";
    state.filter = searchFilterStore.filter;
    state.selectedDictionaryId = undefined;
    state.staticMetadata = {};
    state.temporaryMetadata = {};
    state.section = undefined;
    state.switchedFromDictionaryId = undefined;
    state.scopedSearchMethods = [];
    state.results = undefined;
    state.upsellResults = undefined;
  };

  const direction = computed<string | undefined>(() => {
    if (!state.filter) {
      return;
    }

    if (!selectedDictionary.value) {
      return;
    }

    return SearchFilterHelper.convertToDirection(
      state.filter,
      DictionariesHelper.getMethods([selectedDictionary.value])
    );
  });

  const prioritizedDictionaries = computed<Dictionary[]>(() => {
    const dictionaries = dictionariesStore.dictionaries
      .filter(
        (dictionary) =>
          !dictionariesStore.companyDictionaries.includes(dictionary) &&
          !favoriteDictionariesStore.dictionaries.includes(dictionary)
      )
      .sort((a, b) => a.searchPriority - b.searchPriority);

    return [...dictionariesStore.companyDictionaries, ...favoriteDictionariesStore.dictionaries, ...dictionaries];
  });

  const prioritizedUpsellDictionaries = computed<Dictionary[]>(() =>
    dictionariesStore.upsellDictionaries.sort((a, b) => a.searchPriority - b.searchPriority)
  );

  const prioritizedMethods = computed<DictionaryMethod[]>(() =>
    DictionariesHelper.getMethods(prioritizedDictionaries.value)
  );

  const prioritizedMethodIds = computed<string[]>(() => EntitiesHelper.getIds(prioritizedMethods.value));

  const prioritizedUpsellMethodIds = computed<string[]>(() =>
    EntitiesHelper.getIds(DictionariesHelper.getMethods(prioritizedUpsellDictionaries.value))
  );

  const dictionariesWithPrioritizedResults = computed<Dictionary[]>(() =>
    getFilteredDictionariesWithResults(state.results?.methodIdsWithResult ?? [])
  );

  const favoriteDictionariesWithPrioritizedResults = computed<Dictionary[]>(() =>
    dictionariesWithPrioritizedResults.value.filter((dictionary) =>
      favoriteDictionariesStore.dictionaries.includes(dictionary)
    )
  );

  const upsellDictionariesWithResults = computed<Dictionary[]>(() =>
    getFilteredDictionariesWithResults(state.upsellResults?.methodIdsWithResult ?? [])
  );

  const upsellUnlockedDictionariesWithResults = computed<Dictionary[]>(() =>
    getFilteredDictionariesWithResults(state.upsellResults?.unlockedMethodIdsWithResult ?? [])
  );

  const filteredDictionariesWithPrioritizedResults = computed<Dictionary[]>(() =>
    getFilteredScopedDictionaries(dictionariesWithPrioritizedResults.value, state.results?.methodIdsWithResult ?? [])
  );

  const filteredFavoriteDictionariesWithPrioritizedResults = computed<Dictionary[]>(() =>
    favoriteDictionariesWithPrioritizedResults.value.filter((dictionary) =>
      filteredDictionariesWithPrioritizedResults.value.includes(dictionary)
    )
  );

  const filteredAllSuggestedDictionariesWithPrioritizedResults = computed<Dictionary[]>(() =>
    filteredDictionariesWithPrioritizedResults.value.filter(
      (dictionary) =>
        !favoriteDictionariesStore.dictionaries.includes(dictionary) &&
        !dictionariesStore.companyDictionaries.includes(dictionary)
    )
  );

  const filteredTopSuggestedDictionariesWithPrioritizedResults = computed<Dictionary[]>(() => {
    const resultDictionaries = filteredAllSuggestedDictionariesWithPrioritizedResults.value;
    const topResultDictionaries = resultDictionaries.slice(0, MAX_SUGGESTIONS_COUNT);

    // Add selected dictionary to the top of the list.
    const hasSelectedDictionaryResult =
      EntitiesHelper.findById(resultDictionaries, state.selectedDictionaryId) !== undefined;
    const isSelectedDictionaryPartOfTopResults =
      EntitiesHelper.findById(topResultDictionaries, state.selectedDictionaryId) !== undefined;

    if (selectedDictionary.value && hasSelectedDictionaryResult && !isSelectedDictionaryPartOfTopResults) {
      topResultDictionaries.unshift(selectedDictionary.value);
      topResultDictionaries.pop(); // Respect MAX_SUGGESTIONS_COUNT
    }

    return topResultDictionaries;
  });

  const filteredUpsellMethodIds = computed<string[]>(() => {
    // Handle a corner case where the user attempts to access an upsell dictionary that is outside the filter
    const methodIds = selectedUpsellMethodIds.value ?? [];

    const favoritesWithResults = filteredFavoriteDictionariesWithPrioritizedResults.value.length;
    const suggestedWithResults = filteredTopSuggestedDictionariesWithPrioritizedResults.value.length;

    if (favoritesWithResults + suggestedWithResults > UPSELL_THRESHOLD) {
      return methodIds;
    }

    const filteredUpsellDictionaries = getFilteredScopedDictionaries(
      prioritizedUpsellDictionaries.value,
      prioritizedUpsellMethodIds.value
    );

    return uniq([...methodIds, ...EntitiesHelper.getIds(DictionariesHelper.getMethods(filteredUpsellDictionaries))]);
  });

  const filteredTopUpsellDictionariesWithResults = computed<Dictionary[]>(() => {
    const filteredUpsellDictionariesWithResult = getFilteredScopedDictionaries(
      upsellDictionariesWithResults.value,
      state.upsellResults?.methodIdsWithResult ?? []
    );

    return filteredUpsellDictionariesWithResult.slice(0, UPSELL_MAX_SUGGESTIONS_COUNT);
  });

  const fuzzyRelatedUpsellMethodIdsWithResults = computed<string[]>(() => {
    if (state.results?.type !== "fuzzy" || !selectedDictionary.value || !state.upsellResults) {
      return [];
    }

    const upsellFuzzyMethods = SearchFilterHelper.filterMethods(
      filteredTopUpsellDictionariesWithResults.value,
      SearchFilterHelper.convertToSearchFilter(selectedDictionary.value)
    );

    return EntitiesHelper.getIds(upsellFuzzyMethods);
  });

  const fuzzyRelatedUpsellDictionaryMethodIdsWithResults = computed<string[]>(() => {
    const methodIds = fuzzyRelatedUpsellMethodIdsWithResults.value;
    if (methodIds.length === 0) {
      return [];
    }

    const firstDictionary = dictionariesStore.getDictionaryByMethodId(methodIds[0]);
    if (!firstDictionary) {
      return [];
    }

    const relevantFirstDictionaryMethods = firstDictionary.methods.filter((method) => methodIds.includes(method.id));
    return EntitiesHelper.getIds(relevantFirstDictionaryMethods);
  });

  const selectedDictionary = computed<Dictionary | undefined>(() =>
    EntitiesHelper.findById(dictionariesStore.dictionaries, state.selectedDictionaryId)
  );

  const selectedUpsellDictionary = computed<Dictionary | undefined>(() =>
    EntitiesHelper.findById(dictionariesStore.upsellDictionaries, state.selectedDictionaryId)
  );

  const selectedMethodIds = computed<string[] | undefined>(() => {
    if (!selectedDictionary.value) {
      return undefined;
    }

    return EntitiesHelper.getIds(selectedDictionary.value.methods);
  });

  const selectedUpsellMethodIds = computed<string[] | undefined>(() => {
    if (!selectedUpsellDictionary.value) {
      return undefined;
    }

    return EntitiesHelper.getIds(selectedUpsellDictionary.value.methods);
  });

  const selectedDictionaryMetadata = computed<string[]>(() => {
    const searchDirection = direction.value ?? state.selectedDictionaryId;
    if (!searchDirection) {
      return [];
    }

    const staticMetadata = state.staticMetadata[searchDirection] ?? undefined;
    const temporaryMetadata = state.temporaryMetadata[searchDirection] ?? [];

    return compact([staticMetadata, ...temporaryMetadata]);
  });

  const switchedFromDictionary = computed<Dictionary | undefined>(() =>
    EntitiesHelper.findById(dictionariesStore.dictionaries, state.switchedFromDictionaryId)
  );

  const scopedSearchDictionaries = computed<Dictionary[]>(() => {
    const dictionaries = state.scopedSearchMethods.map((method) =>
      dictionariesStore.getDictionaryByMethodId(method.id)
    );
    return compact(uniqBy(dictionaries, "id"));
  });

  const articles = computed<Article[]>(() => {
    if (state.results?.type !== "exact") {
      return [];
    }

    const companyDictionaryMethodIds = EntitiesHelper.getIds(
      DictionariesHelper.getMethods(dictionariesStore.companyDictionaries)
    );

    const favoriteDictionaryMethodIds = EntitiesHelper.getIds(
      DictionariesHelper.getMethods(favoriteDictionariesStore.dictionaries)
    );

    // The articles are prioritized from the backend, but changing the favorites should trigger an immediate re-order
    return uniq([
      ...state.results.articles.filter((article) => companyDictionaryMethodIds.includes(article.methodId)),
      ...state.results.articles.filter((article) => favoriteDictionaryMethodIds.includes(article.methodId)),
      ...state.results.articles
    ]);
  });

  const hasUpsellArticles = computed<boolean>(() =>
    state.upsellResults ? state.upsellResults.articles.length > 0 : false
  );

  const upsellArticles = computed<UpsellArticle[]>(() => (state.upsellResults ? state.upsellResults.articles : []));

  const groupedUpsellArticles = computed<UpsellArticleGroup[]>(() =>
    upsellArticles.value.reduce<UpsellArticleGroup[]>((groups, article) => {
      const dictionary = dictionariesStore.getDictionaryByMethodId(article.methodId);
      if (!dictionary) {
        return groups;
      }

      const group = groups.find((g) => g.dictionary === dictionary);

      if (group) {
        group.articles.push(article);
      } else {
        groups.push({
          dictionary,
          articles: [article]
        });
      }

      return groups;
    }, [])
  );

  const isLoading = computed<boolean>(() => state.status === "loading");
  const isLoaded = computed<boolean>(() => state.status === "loaded" && state.results !== undefined);
  const isFetchingArticle = computed<boolean>(() => state.status === "loading" && state.results !== undefined);

  const getFilteredDictionariesWithResults = (methodIdsWithResult: string[]): Dictionary[] =>
    methodIdsWithResult.reduce<Dictionary[]>((result, methodId) => {
      const dictionary = dictionariesStore.getDictionaryByMethodId(methodId);

      if (dictionary && !result.includes(dictionary)) {
        result.push(dictionary);
      }

      return result;
    }, []);

  const getFilteredScopedDictionaries = (dictionaries: Dictionary[], allowedMethodIds: string[]): Dictionary[] =>
    dictionaries.reduce<Dictionary[]>((result, dictionary) => {
      if (!state.filter?.sourceLanguage) {
        result.push(dictionary);
        return result;
      }

      const filteredMethodIds = SearchFilterHelper.filterMethods([dictionary], state.filter).map((method) => method.id);
      const filteredAllowedMethodIds = intersection(allowedMethodIds, filteredMethodIds);

      if (filteredAllowedMethodIds.length > 0) {
        result.push(dictionary);
      }

      return result;
    }, []);

  const getScopedSearchMethods = (searchAllDictionaries: boolean) =>
    // The scoped methods are prioritized with the selected dictionary first, as we try to keep
    // the user in the already selected dictionary. If we allow switching dictionary, we ensure
    // all other dictionaries are added to the list as well. Everything is filtered by language,
    // if the language selector is active
    uniq(
      SearchFilterHelper.filterMethods(
        compact([selectedDictionary.value, ...(searchAllDictionaries ? prioritizedDictionaries.value : [])]),
        searchFilterStore.filter,
        compact([
          ...(selectedDictionary.value?.methods ?? []),
          ...(searchAllDictionaries ? prioritizedMethods.value : [])
        ])
      )
    );

  const setNewTitle = (newTitle: string) => {
    if (!newTitle) {
      return;
    }

    document.title = `${newTitle} - ${defaultTitle}`;
  };

  const flushResults = () => {
    state.results = undefined;
    state.upsellResults = undefined;
  };

  const flushArticles = () => {
    if (state.results?.type === "exact") {
      state.results.articles = [];
    }

    if (state.upsellResults) {
      state.upsellResults.articles = [];
    }
  };

  const classicSearch = async (
    query: string,
    filter: SearchFilter,
    dictionaryId?: string,
    staticMetadata?: Record<string, string>,
    temporaryMetadata?: Record<string, string[]>,
    section?: string,
    allowSwitchDictionary = true
  ): Promise<void> => {
    // A new search requires removing the previous search result manually, as
    // methods with results are kept as long as possible, for performance
    if (
      SearchHelper.hasQueryChanged(state.query, query) ||
      SearchHelper.hasFilterChanged(searchFilterStore.filter, filter)
    ) {
      flushResults();
    }

    // This is also true, when selecting a dictionary updates the language filter
    const languageFilterWasChanged = SearchHelper.hasFilterChanged(state.filter, filter);

    flushArticles();

    state.query = query;
    state.filter = { ...filter };
    state.selectedDictionaryId = dictionaryId;
    state.staticMetadata = staticMetadata ?? {};
    state.temporaryMetadata = temporaryMetadata ?? {};
    state.switchedFromDictionaryId = undefined;

    // Ensure computeds have updated based on new state
    await flushVueQueue();

    state.scopedSearchMethods = getScopedSearchMethods(allowSwitchDictionary);
    const prioritizedScopedMethodIds = EntitiesHelper.getIds(state.scopedSearchMethods);

    const metadata = SearchHelper.mergeSearchMetadata(state.staticMetadata, state.temporaryMetadata);

    const prevStatus = state.status;
    const hasResults = state.results !== undefined;

    // Used to scope how many dictionaries we search in. Either we want to search in all methods
    // to find out which have results, or we want to build on the existing search result by
    // limiting the search to the result methods only, which is much better for performance
    const buildOnExistingResult = hasResults && !allowSwitchDictionary;
    const searchMethodIds = buildOnExistingResult ? prioritizedScopedMethodIds : prioritizedMethodIds.value;
    const resultMethodIds = prioritizedScopedMethodIds;

    setNewTitle(state.query);

    // Abort search, if have a search result and the selected dictionary is an ad aka static "article"
    if (buildOnExistingResult && state.selectedDictionaryId === companyDictionaryAdId) {
      return;
    }

    try {
      state.status = "loading";

      // User has selected an upsell dictionary where we know that there is a result
      if (buildOnExistingResult && selectedUpsellMethodIds.value) {
        await upsellSearch(query, selectedUpsellMethodIds.value, selectedUpsellMethodIds.value, metadata);
        state.status = "loaded";
        return;
      }

      // Search and result method ids are prioritized, and the API maintains that priority for the
      // returning results.methodIdsWithResult
      const results = await classicSearchSingleton(query, metadata, searchMethodIds, resultMethodIds);

      // When we only search in the result methods only, it might be due to change in metadata.
      // If that is so, dictionaries that had result with a different metadata, will need to be
      // re-evaluated if there is still a result. Switching between metadata can trigger results
      // and no results – therefore we need both cases
      if (state.results && buildOnExistingResult) {
        // Add the search results methodIds to the end of the current methodIds,
        // This makes sure the order of the methods will change as little as possible.
        if (results.type === "exact") {
          results.methodIdsWithResult = uniq([...state.results.methodIdsWithResult, ...results.methodIdsWithResult]);
        } else {
          results.methodIdsWithResult = uniq([
            ...results.methodIdsWithResult,
            ...difference(state.results.methodIdsWithResult, buildOnExistingResult ? prioritizedScopedMethodIds : [])
          ]);
        }
      }

      state.results = results;

      if (allowSwitchDictionary || !state.selectedDictionaryId) {
        let methodId = "";
        if (state.results?.type === "exact") {
          methodId = state.results.articles?.[0].methodId;
        } else if (state.results?.type === "fuzzy") {
          methodId = state.results.methodIds[0];
        }

        if (methodId) {
          state.selectedDictionaryId = dictionariesStore.getDictionaryByMethodId(methodId)?.id;
        }

        if (state.selectedDictionaryId !== dictionaryId) {
          state.switchedFromDictionaryId = dictionaryId;

          await searchRouteHelper.selectDictionary(
            state.selectedDictionaryId,
            state.query,
            direction.value,
            allowSwitchDictionary,
            "replace"
          );
        }
      }

      if ((!buildOnExistingResult || languageFilterWasChanged) && filteredUpsellMethodIds.value) {
        // If this was triggered by a language filter change, then we reset existing upsell results
        if (languageFilterWasChanged) {
          state.upsellResults = undefined;
        }

        await upsellSearch(query, filteredUpsellMethodIds.value, selectedUpsellMethodIds.value ?? [], metadata);
      }

      if (fuzzyRelatedUpsellDictionaryMethodIdsWithResults.value) {
        await upsellSearch(
          query,
          fuzzyRelatedUpsellDictionaryMethodIdsWithResults.value,
          fuzzyRelatedUpsellDictionaryMethodIdsWithResults.value,
          metadata
        );
      }

      // Make sure the log API request isn't cancelled upon search redirection.
      suggestionsStore.logStoredSuggestionSelection();

      // Scroll to element, when DOM is updated.
      if (section) {
        requestAnimationFrame(() => document.getElementById(section)?.scrollIntoView({ behavior: "smooth" }));
      }

      userStore.userData.freeSearchesLeft = results.freeSearchesLeft;
      state.status = "loaded";
    } catch (e) {
      if (e instanceof Cancellation) {
        state.status = prevStatus;
        return;
      }

      state.status = "failed";
      throw e;
    }
  };

  const compactSearchEventSource = ref<SSE | undefined>();

  const compactSearch = async (query: string, filter: SearchFilter, dictionaryId?: string): Promise<void> => {
    if (SearchHelper.hasQueryChanged(state.query, query) || SearchHelper.hasFilterChanged(state.filter, filter)) {
      flushResults();
    }

    state.query = query;
    state.filter = { ...filter };
    state.selectedDictionaryId = dictionaryId;

    // Ensure computeds have updated based on new state
    await flushVueQueue();

    // Abort search, if have a search result and the selected dictionary is an ad aka static "article"
    if (state.results && state.selectedDictionaryId === companyDictionaryAdId) {
      return;
    }

    // If we already have articles for the filtered dictionary, then we don't need to search again
    if (
      SearchHelper.hasAnyMethodIdsExactArticleResults(selectedMethodIds.value, state.results) ||
      SearchHelper.hasAnyMethodIdsUpsellArticleResults(selectedUpsellMethodIds.value, state.upsellResults)
    ) {
      return;
    }

    flushArticles();

    state.scopedSearchMethods = getScopedSearchMethods(true);
    const prioritizedScopedMethodIds = EntitiesHelper.getIds(state.scopedSearchMethods);

    let searchMethodIds = prioritizedScopedMethodIds;
    let resultMethodIds = prioritizedScopedMethodIds;

    // If we have searched, but the filtered dictionary had no results, then we need to force fuzzy
    if (SearchHelper.hasAllMethodIdsNoResult(selectedMethodIds.value, state.results)) {
      searchMethodIds = selectedMethodIds.value ?? [];
      resultMethodIds = selectedMethodIds.value ?? [];
    }

    // If we have filtered dictionary, but no results yet, then we need to do a full search but, forced result
    if (selectedMethodIds.value && selectedMethodIds.value.length > 0 && !state.results) {
      resultMethodIds = selectedMethodIds.value;
    }

    setNewTitle(state.query);

    state.status = "loading";
    compactSearchEventSource.value?.close();

    let articleCount = 0;
    let articleBatchCache: Article[] = [];

    // Search and result method ids are prioritized, and the API maintains that priority for the
    // result.methodIdsWithResult event
    compactSearchEventSource.value = SearchService.compactSearch(query, searchMethodIds, resultMethodIds);

    compactSearchEventSource.value.addEventListener("searchResult", (e) => {
      const event = e as CustomEventDataType;
      const result = JSON.parse(event.data) as SearchResults;

      if (result.type === "error") {
        if (result.message === "search_view_type_not_available") {
          compactSearchEventSource.value?.close();
          window.location.reload();
          return;
        }

        state.status = "failed";
        state.results = result;
      }

      // Ensure existing method ids with results are kept, as searched with filtered dictionary might be limited in scope
      if (selectedMethodIds.value && state.results) {
        result.methodIdsWithResult = state.results.methodIdsWithResult;
      }

      state.results = result;
      userStore.userData.freeSearchesLeft = result.freeSearchesLeft;
    });

    compactSearchEventSource.value.addEventListener("article", (e) => {
      if (state.results?.type !== "exact") {
        return;
      }

      const event = e as CustomEventDataType;
      const article = JSON.parse(event.data) as Article;

      // Push the first 10 articles immediately for perceived performance, then batch 10 at a time to avoid locking up the browser
      if (articleCount < 10) {
        state.results.articles.push(article);
        articleCount += 1;
        return;
      }

      articleBatchCache.push(article);
      if (articleBatchCache.length >= 10) {
        state.results.articles.push(...articleBatchCache);
        articleBatchCache = [];
      }
    });

    compactSearchEventSource.value.addEventListener("end", () => {
      // Delay upsell to avoid search logging race-condition
      if (filteredUpsellMethodIds.value) {
        upsellSearch(query, filteredUpsellMethodIds.value, filteredUpsellMethodIds.value);
      }

      if (state.results?.type === "exact") {
        // Push any remaining articles from the last batch
        state.results?.articles.push(...articleBatchCache);
        articleBatchCache = [];
      }

      state.status = "loaded";
      compactSearchEventSource.value?.close();

      // Make sure the log API request isn't cancelled upon search redirection.
      suggestionsStore.logStoredSuggestionSelection();
    });

    compactSearchEventSource.value.addEventListener("error", (e) => {
      if (!isBackendError(e)) {
        // Probably thrown by the connection being closed, nothing to do
        return;
      }

      state.status = "failed";
    });

    compactSearchEventSource.value.stream();
  };

  function isBackendError(error: CustomEvent): error is CustomEventDataType {
    return Object.prototype.hasOwnProperty.call(error, "data");
  }

  const upsellSearch = async (
    query: string,
    searchMethodIds: string[],
    resultMethodIds: string[],
    metadata: Record<string, string[]> = {}
  ): Promise<void> => {
    if (searchMethodIds.length === 0) {
      return;
    }

    try {
      // Search and result method ids are prioritized, and the API maintains that priority for the
      // returning method ids lists within the result
      const result = await upsellSearchSingleton(query, metadata, searchMethodIds, resultMethodIds);

      if (state.upsellResults) {
        // For dictionaries with metadata, we might encounter a case where the dictionary had results
        // initially, but not after switching metadata. We therefore remove any methods that no longer
        // has a result, aligning the upsell behaviour with the one for normal dictionaries
        const methodIdsWithoutResult = difference(resultMethodIds, result.methodIdsWithResult);
        if (methodIdsWithoutResult) {
          state.upsellResults.methodIdsWithResult = state.upsellResults.methodIdsWithResult.filter(
            (methodId) => !methodIdsWithoutResult.includes(methodId)
          );
        }

        // Switching from a no result metadata to a result metadata requires us to add the methods
        state.upsellResults.methodIdsWithResult = uniq([
          ...state.upsellResults.methodIdsWithResult,
          ...result.methodIdsWithResult
        ]);

        state.upsellResults.articles = result.articles;
      } else {
        state.upsellResults = result;
      }
    } catch (e) {
      if (e instanceof Cancellation) {
        return;
      }

      throw e;
    }
  };

  const upsellUnlock = async (dictionary: Dictionary): Promise<void> => {
    const methodIds = EntitiesHelper.getIds(
      dictionary.methods.filter(
        (method) =>
          state.upsellResults?.methodIdsWithResult?.includes(method.id) &&
          state.upsellResults?.unlockableMethodIdsWithResult?.includes(method.id)
      )
    );

    if (methodIds.length === 0) {
      return;
    }

    const prevStatus = state.status;

    try {
      state.status = "loading";

      const results = await upsellSearchSingleton(
        state.query,
        SearchHelper.mergeSearchMetadata(state.staticMetadata, state.temporaryMetadata),
        methodIds,
        methodIds,
        true
      );

      // Replacing unlocked articles in-place
      results.articles.forEach((result) => {
        if (!state.upsellResults) {
          return;
        }

        const index = state.upsellResults.articles.findIndex((article) => article.methodId === result.methodId);
        if (index >= 0) {
          state.upsellResults.articles.splice(index, 1, result);
        } else {
          state.upsellResults.articles.push(result);
        }

        state.upsellResults.unlockedMethodIdsWithResult.push(result.methodId);
      });

      state.status = "loaded";
    } catch (e) {
      if (e instanceof Cancellation) {
        state.status = prevStatus;
        return;
      }

      state.status = "failed";
      throw e;
    }
  };

  return {
    ...toRefs(state),
    reset,
    direction,
    dictionariesWithPrioritizedResults,
    filteredDictionariesWithPrioritizedResults,
    upsellUnlockedDictionariesWithResults,
    filteredFavoriteDictionariesWithPrioritizedResults,
    filteredAllSuggestedDictionariesWithPrioritizedResults,
    filteredTopSuggestedDictionariesWithPrioritizedResults,
    filteredTopUpsellDictionariesWithResults,
    articles,
    hasUpsellArticles,
    upsellArticles,
    groupedUpsellArticles,
    selectedDictionary,
    selectedUpsellDictionary,
    selectedMethodIds,
    selectedUpsellMethodIds,
    fuzzyRelatedUpsellMethodIdsWithResults,
    selectedDictionaryMetadata,
    switchedFromDictionary,
    prioritizedMethodIds,
    scopedSearchDictionaries,
    isLoading,
    isLoaded,
    isFetchingArticle,
    flushResults,
    classicSearch,
    compactSearch,
    upsellUnlock
  };
});

if (import.meta.hot) {
  import.meta.hot.accept(acceptHMRUpdate(useSearchStore, import.meta.hot));
}
